<template>
  <div>
    <div class="row">
      <div class="col-12">
        <h2>Add Device</h2>
        <hr />
      </div>
    </div>
    <div class="row">
      <div class="col-8">
        <form>
          <div class="form-group">
            <label for="txtName">Name</label>
            <input type="text" class="form-control" id="txtName" v-model="device.name" />
          </div>
          <div class="form-group">
            <label for="ddlDeviceType">Device Type</label>
            <select
              class="form-control"
              id="ddlDeviceType"
              v-model="device.device_type_id"
              @change="deviceTypeChange"
            >
              <option
                v-for="device_type in filtered_device_types"
                :key="device_type.device_type_id"
                v-bind:value="device_type.device_type_id"
              >{{ device_type.name }}</option>
            </select>
          </div>
          <div class="form-group" v-if="isSip">
            <label for="ddlDeviceSipType">Sip Type</label>
            <select
              class="form-control"
              id="ddlDeviceSipType"
              v-model="device.device_sip_type_id"
              @change="deviceSipTypeChange"
            >
              <option
                v-for="device_sip_type in device_sip_types"
                :key="device_sip_type.device_sip_type_id"
                v-bind:value="device_sip_type.device_sip_type_id"
              >{{ device_sip_type.name }}</option>
            </select>
          </div>
          <div class="form-check" v-if="showVoip">
            <input
              type="checkbox"
              class="form-check-input"
              v-model="device.voip"
              @change="deviceVoipChange"
            />
            <label class="form-check-label" for="chkVoip">VoIP</label>
          </div>
          <div class="form-group" v-if="isSip && device.device_sip_type_id === 6">
            <label for="ddlIntercomExternalSip">Intercom External SIP</label>
            <select
              class="form-control"
              id="ddlIntercomExternalSip"
              v-model="device.intercom_external_sip_id"
            >
              <option value="0">Select..</option>
              <option
                v-for="intercom_external_sip in intercom_external_sips"
                :key="intercom_external_sip.intercom_external_sip_id"
                v-bind:value="intercom_external_sip.intercom_external_sip_id"
              >{{ intercom_external_sip.name }}</option>
            </select>
          </div>
          <div class="form-group" v-if="showZones">
            <label for="ddlZones">Zones</label>
            <v-select multiple v-model="zones_selected" :options="zones_for_drop_down"></v-select>
          </div>
          <div class="form-group">
            <label for="txtIp">IP</label>
            <input type="text" class="form-control" id="txtIp" v-model="device.ip" />
          </div>
          <div class="form-group" v-if="showPort">
            <label for="txtPort">Port</label>
            <input type="text" class="form-control" id="txtPort" v-model="device.port" />
          </div>
          <ExtensionField
            v-model="device.extension"
            v-if="isSip"
            :helper_text="'Must be four digits. Cannot start with a 6.'"
          ></ExtensionField>
          <div
            class="form-group"
            v-for="device_attribute in device.device_attributes"
            :key="device_attribute.attr_name"
          >
            <label :for="'txt' + device_attribute.attr_name">{{ device_attribute.attr_name }}</label>
            <div
              v-if="device_attribute.attr_name === 'DID Number' || device_attribute.attr_name === 'Forward Number'"
            >
              <vue-tel-input
                :onlyCountries="['US']"
                :validCharactersOnly="true"
                v-model="device_attribute.attr_str"
              ></vue-tel-input>
            </div>
            <div v-else>
              <input
                type="text"
                class="form-control"
                :id="'txt' + device_attribute.attr_name"
                v-model="device_attribute.attr_str"
              />
            </div>
          </div>
          <button type="button" class="btn btn-hub-primary" @click="submit()">Submit</button>
          <router-link :to="{ name: 'Devices' }" class="btn btn-secondary">Cancel</router-link>
        </form>
      </div>
      <div class="col-4" v-if="isSip">
        <CurrentExtensions :default_type="'device'"></CurrentExtensions>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import CurrentExtensions from '../../components/CurrentExtensions'
import ExtensionField from '../../components/ExtensionField'

export default {
  components: { vSelect, CurrentExtensions, ExtensionField },
  data: function () {
    return {
      zones_selected: [
        {
          value: 0,
          label: 'All Call - District & Site'
        }
      ],
      device: {
        device_id: 0,
        name: '',
        device_type_id: 1,
        device_sip_type_id: 1,
        intercom_external_sip_id: '0',
        ip: '',
        port: '4444',
        extension: 0,
        enabled: 1,
        voip: 0,
        device_zones: [],
        device_attributes: []
      }
    }
  },
  computed: {
    ...mapGetters({
      zones: 'allZones',
      device_types: 'allDeviceTypes',
      device_sip_types: 'allDeviceSipTypes',
      intercom_external_sips: 'allIntercomExternalSips',
      scoped_site_id: 'scopedSiteId',
      feature_visual_alert_enabled: 'featureVisualAlertEnabled',
      feature_voip_enabled: 'featureVoipEnabled'
    }),
    filtered_device_types: function () {
      let toExcludeIds = [3]

      if (!this.feature_visual_alert_enabled) {
        toExcludeIds.push(5)
      }

      return this.device_types.filter(
        dt => !toExcludeIds.includes(dt.device_type_id)
      )
    },
    showPort () {
      return [1, 3, 4].includes(this.device.device_type_id)
    },
    showVoip () {
      return (
        this.feature_voip_enabled &&
        this.device.device_type_id === 2 &&
        this.device.device_sip_type_id === 1
      )
    },
    showZones () {
      return ![4, 5].includes(this.device.device_type_id)
    },
    isSip () {
      return this.device.device_type_id === 2
    },
    zones_for_drop_down: function () {
      if (this.device.device_type_id === 5) {
        return this.zones
          .filter(
            zone =>
              zone.site_id === this.scoped_site_id && zone.zone_type_id === 4
          )
          .map(zone => {
            return {
              value: zone.zone_id,
              label: zone.name
            }
          })
      } else {
        return this.zones
          .filter(
            zone =>
              zone.site_id === this.scoped_site_id && zone.zone_type_id !== 4
          )
          .map(zone => {
            return {
              value: zone.zone_id,
              label: zone.name
            }
          })
      }
    }
  },
  created () {
    this.$store.dispatch('setDisabledChangeScopedSite', true)
    this.$store.dispatch('getZones')
    this.$store.dispatch('getDeviceTypes')
    this.$store.dispatch('getDeviceSipTypes')
    this.$store.dispatch('getIntercomExternalSips')
    this.$store.dispatch('getPhoenixSettings', 'PHOENIX.FEATURE')
  },
  methods: {
    deviceTypeChange () {
      this.device.voip = 0

      if ([4, 5].includes(this.device.device_type_id)) {
        this.zones_selected = []

        if (this.device.device_type_id === 4) {
          this.device.port = '12302'
          this.device.device_attributes = []
          let attributes = new Map()

          attributes = new Map([
            ['Public IP', '']
          ])

          for (let [attrName, attrStr] of attributes) {
            this.device.device_attributes.push({
              attr_name: attrName,
              attr_str: attrStr
            })
          }
        }
      } else {
        this.zones_selected = [
          {
            value: 0,
            label: 'All Call - District & Site'
          }
        ]
      }

      if ([2, 5].includes(this.device.device_type_id)) {
        this.device.ip = '1.1.1.1'
      } else {
        this.device.ip = ''
      }
    },
    deviceSipTypeChange () {
      this.device.voip = 0
      this.device.device_attributes = []
      let attributes = new Map()

      if (this.device.device_sip_type_id === 7) {
        attributes = new Map([
          ['Command Password', 'Everest'],
          ['Dev Password', 'verifybravo']
        ])
      } else if (this.device.device_sip_type_id === 8) {
        attributes = new Map([
          ['MAC Address', ''],
          ['WAN Mode', ''],
          ['WAN Subnet Mask', ''],
          ['WAN Gateway', ''],
          ['Primary DNS', ''],
          ['Secondary DNS', ''],
          ['SNTP Server', ''],
          ['Second SNTP Server', ''],
          ['Enable SNTP', ''],
          ['Time Zone', ''],
          ['Location', ''],
          ['Time Zone Name', ''],
          ['Enable DST', ''],
          ['General Mic Volume', ''],
          ['Broadcast Speaker Volume', ''],
          ['Register TTL', ''],
          ['Enable Registration', ''],
          ['Building Number', ''],
          ['Func 1 Value', ''],
          ['Func 2 Value', '']
        ])
      }

      for (let [attrName, attrStr] of attributes) {
        this.device.device_attributes.push({
          attr_name: attrName,
          attr_str: attrStr
        })
      }
    },
    deviceVoipChange () {
      this.device.device_attributes = []
      let attributes = new Map()

      if (this.device.voip) {
        attributes = new Map([
          ['DID Number', ''],
          ['Forward Number', '']
        ])
      }

      for (let [attrName, attrStr] of attributes) {
        this.device.device_attributes.push({
          attr_name: attrName,
          attr_str: attrStr
        })
      }
    },
    submit () {
      for (let i = 0; i < this.zones_selected.length; i++) {
        this.device.device_zones.push({
          zone_id: this.zones_selected[i].value
        })
      }
      this.$store.dispatch('addEditDevice', {
        device: this.device,
        push_on_success: 'Devices'
      })
    }
  }
}
</script>

<style scoped>
</style>
